'use client';

import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useLazyQuery, useMutation } from '@parkchirp/api-client';
import React, { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ASSIGN_PARKER_TO_RESERVED_SPACE } from '../../../mutations';
import { GET_AVAILABLE_RESERVED_SPACE_INVENTORY_BY_SUBLOT } from '../../../queries';
import { SetShowSnackbar } from '../../../types';
import { Dayjs, dayjs, isReservedSpaceRangeReserved } from '../../../utils';

type AssignReservedSpaceModalProps = {
	parkerId: string;
	sublotId: string;
	reservedSpaces: {
		id: string;
		spaceNumber: string;
		level: string;
		startDate: string;
		endDate: string | null;
	}[];
	closeModal: () => void;
	setShowSnackbar: SetShowSnackbar;
	onAssigned?: () => void;
	isNewParker?: boolean;
	pendingReservedSpaceAssignment?: StagedReservedSpaceAssignment[] | null;
	onStageAssignment?: (assignment: StagedReservedSpaceAssignment[] | null) => void;
};

export type StagedReservedSpaceAssignment = {
	id: string;
	reservedSpaceId: number;
	level: string;
	spaceNumber: string;
	startDate: string;
	endDate: string | null;
};

type GetAvailableReservedSpaceInventoryBySublotResponse = {
	getAvailableReservedSpaceInventoryBySublot: {
		reservedSpaces: {
			id: number;
			spaceNumber: string;
			level: string;
		}[];
	};
};

const steps = ['Select a date', 'Select a space'];

export const AssignReservedSpaceModal = ({
	parkerId,
	sublotId,
	reservedSpaces,
	closeModal,
	setShowSnackbar,
	onAssigned,
	isNewParker,
	pendingReservedSpaceAssignment,
	onStageAssignment,
}: AssignReservedSpaceModalProps) => {
	const [activeStep, setActiveStep] = useState<number>(0);
	const [startDate, setStartDate] = useState<string | null>(() => {
		const initialReservedSpaces =
			isNewParker && pendingReservedSpaceAssignment ? pendingReservedSpaceAssignment : reservedSpaces;
		const today = dayjs.utc().startOf('day');
		return isReservedSpaceRangeReserved(initialReservedSpaces, today, today) ? null : today.format();
	});
	const [endDate, setEndDate] = useState<string | null>(null);
	const [noEndDate, setNoEndDate] = useState<boolean>(true);
	const [level, setLevel] = useState<string | null>(null);
	const [spaceNumber, setSpaceNumber] = useState<string>('');
	const [saving, setSaving] = useState<boolean>(false);

	const [getAvailableReservedSpaceInventoryBySublot, { data: availableSpacesData, loading: availableSpacesLoading }] =
		useLazyQuery<GetAvailableReservedSpaceInventoryBySublotResponse>(
			GET_AVAILABLE_RESERVED_SPACE_INVENTORY_BY_SUBLOT,
		);

	const handleNext = useCallback(async () => {
		await getAvailableReservedSpaceInventoryBySublot({
			variables: {
				input: {
					sublotId: Number(sublotId),
					startDate,
					...(endDate ? { endDate } : {}),
				},
			},
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
		});
		setActiveStep((prev) => prev + 1);
	}, [sublotId, startDate, endDate, getAvailableReservedSpaceInventoryBySublot]);

	const handleBack = useCallback(() => {
		setActiveStep((prev) => prev - 1);
		setLevel('');
		setSpaceNumber('');
	}, []);

	const effectiveReservedSpaces =
		isNewParker && pendingReservedSpaceAssignment ? pendingReservedSpaceAssignment : reservedSpaces;
	const isStartDateReserved = useCallback(
		(date: Dayjs) => {
			const day = date.startOf('day');
			return isReservedSpaceRangeReserved(effectiveReservedSpaces, day, day);
		},
		[effectiveReservedSpaces],
	);

	const isEndDateReserved = useCallback(
		(date: Dayjs) => {
			if (!startDate) {
				return true;
			}
			const effectiveStartDate = dayjs.utc(startDate).startOf('day');
			return isReservedSpaceRangeReserved(effectiveReservedSpaces, effectiveStartDate, date.startOf('day'));
		},
		[effectiveReservedSpaces, startDate],
	);

	const getMinEndDate = useCallback(() => {
		return startDate ? dayjs.utc(startDate).add(1, 'day') : undefined;
	}, [startDate]);

	const getInactiveDateReference = () => {
		if (startDate) {
			return dayjs(startDate).add(1, 'day').startOf('day');
		}
		return dayjs.utc().startOf('day');
	};
	const levelOptions =
		availableSpacesData?.getAvailableReservedSpaceInventoryBySublot?.reservedSpaces
			.filter((space) => !spaceNumber || space.spaceNumber === spaceNumber)
			.map((space) => space.level)
			.filter((lvl, index, self) => lvl && self.indexOf(lvl) === index) || [];

	const selectedSpaceRequiresLevel = Boolean(spaceNumber) && levelOptions.length > 0;
	const isSaveDisabled = !spaceNumber || (selectedSpaceRequiresLevel && !level);

	const [assignParkerToReservedSpace] = useMutation(ASSIGN_PARKER_TO_RESERVED_SPACE, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
	});

	const handleSave = useCallback(async () => {
		const selectedSpace = availableSpacesData?.getAvailableReservedSpaceInventoryBySublot?.reservedSpaces.find(
			(space) => space.level === level && space.spaceNumber === spaceNumber,
		);

		if (isNewParker) {
			const updatedAssignments = [
				...(pendingReservedSpaceAssignment || []),
				{
					id: uuidv4(),
					reservedSpaceId: selectedSpace?.id,
					level,
					spaceNumber,
					startDate,
					endDate: endDate || null,
				},
			];
			onStageAssignment(updatedAssignments);
			setShowSnackbar({
				message: 'Reserved space selected. It will be assigned when the parker is created.',
				severity: 'success',
			});
			closeModal();
			return;
		}

		setSaving(true);
		try {
			await assignParkerToReservedSpace({
				variables: {
					input: {
						parkerId,
						reservedSpaceId: selectedSpace?.id,
						startDate,
						endDate: endDate || null,
					},
				},
			});
			setShowSnackbar({
				message: 'Reserved space assigned successfully',
				severity: 'success',
			});
			onAssigned?.();
			closeModal();
		} catch (err) {
			setShowSnackbar({
				message: err.message,
				severity: 'error',
			});
		} finally {
			setSaving(false);
		}
	}, [
		setShowSnackbar,
		startDate,
		endDate,
		level,
		spaceNumber,
		parkerId,
		availableSpacesData,
		assignParkerToReservedSpace,
		closeModal,
		onAssigned,
		onStageAssignment,
		pendingReservedSpaceAssignment,
		isNewParker,
	]);

	return (
		<Grid container direction='column' rowSpacing={3}>
			<Grid item>
				<Typography variant='h6'>Assign Reserved Space</Typography>
			</Grid>
			<Grid item>
				<Stepper activeStep={activeStep}>
					{steps.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
			</Grid>
			{activeStep === 0 && (
				<Grid item>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Grid container spacing={2} justifyContent='center'>
							<Grid item xs={6} textAlign='center'>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DatePicker<Dayjs>
										label='Start Date'
										value={startDate ? dayjs.utc(startDate) : null}
										shouldDisableDate={isStartDateReserved}
										onChange={(value) => {
											setStartDate(value.format());
											setEndDate(null);
											setNoEndDate(true);
										}}
										sx={{ width: '100%', outlineStyle: 'none' }}
										slotProps={{
											textField: {
												inputProps: {
													'data-cy': 'reservedSpace-startDate',
													disabled: true,
												},
												variant: 'standard',
											},
										}}
									/>
								</LocalizationProvider>
							</Grid>
							<Grid item xs={6} textAlign='center'>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DatePicker<Dayjs>
										label='End Date'
										disabled={!startDate}
										value={dayjs(endDate).isValid() ? dayjs.utc(endDate) : null}
										minDate={getMinEndDate()}
										shouldDisableDate={(date) => isEndDateReserved(date)}
										referenceDate={getInactiveDateReference()}
										onChange={(value) => {
											setEndDate(value ? value.format() : null);
											setNoEndDate(!value);
										}}
										sx={{ width: '100%', outlineStyle: 'none' }}
										slotProps={{
											textField: {
												inputProps: {
													'data-cy': 'reservedSpace-endDate',
													disabled: true,
												},
												variant: 'standard',
											},
										}}
									/>
								</LocalizationProvider>
								<FormControlLabel
									control={
										<Checkbox
											checked={noEndDate}
											onChange={(e) => {
												setNoEndDate(e.target.checked);
												if (e.target.checked) setEndDate(null);
											}}
											inputProps={
												{
													'data-cy': 'reservedSpace-noEndDate',
												} as React.InputHTMLAttributes<HTMLInputElement>
											}
										/>
									}
									label='No end date'
								/>
							</Grid>
						</Grid>
					</LocalizationProvider>
					<Grid container justifyContent='space-between' pt={3}>
						<Button onClick={closeModal}>Cancel</Button>
						<Button onClick={handleNext} disabled={availableSpacesLoading || !startDate}>
							{availableSpacesLoading ? <CircularProgress size={25} sx={{ color: 'white' }} /> : 'Next'}
						</Button>
					</Grid>
				</Grid>
			)}
			{activeStep === 1 &&
				(saving ? (
					<Grid textAlign='center' pt={4}>
						<CircularProgress />
					</Grid>
				) : (
					<Grid item>
						<Grid container spacing={2} justifyContent='center'>
							<Grid item xs={6} textAlign='center'>
								<Autocomplete
									id='reservedSpace-level-autocomplete'
									options={levelOptions}
									value={level}
									onChange={(_, newValue) => {
										setLevel(newValue);
										if (newValue && spaceNumber) {
											const valid =
												availableSpacesData?.getAvailableReservedSpaceInventoryBySublot?.reservedSpaces.some(
													(space) =>
														space.level === newValue && space.spaceNumber === spaceNumber,
												);
											if (!valid) setSpaceNumber('');
										}
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label='Level'
											inputProps={{
												...params.inputProps,
												'data-cy': 'reservedSpace-level-autocomplete',
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={6} textAlign='center'>
								<Autocomplete
									id='reservedSpace-spacenumber-autocomplete'
									options={
										availableSpacesData?.getAvailableReservedSpaceInventoryBySublot?.reservedSpaces
											.filter((space) => !level || space.level === level)
											.map((space) => space.spaceNumber)
											.filter((sn, index, self) => sn && self.indexOf(sn) === index) || []
									}
									value={spaceNumber}
									onChange={(_, newValue) => {
										setSpaceNumber(newValue);
										if (newValue && level) {
											const valid =
												availableSpacesData?.getAvailableReservedSpaceInventoryBySublot?.reservedSpaces.some(
													(space) => space.spaceNumber === newValue && space.level === level,
												);
											if (!valid) setLevel('');
										}
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label='Space Number'
											inputProps={{
												...params.inputProps,
												'data-cy': 'reservedSpace-spacenumber-autocomplete',
											}}
										/>
									)}
								/>
							</Grid>
							<Grid container justifyContent='space-between' pt={3}>
								<Box>
									<Button onClick={handleBack}>Back</Button>
								</Box>
								<Box>
									<Button
										onClick={handleSave}
										disabled={isSaveDisabled}
										data-cy='reservedSpace-saveButton'
									>
										Save
									</Button>
								</Box>
							</Grid>
						</Grid>
					</Grid>
				))}
		</Grid>
	);
};
